import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

/**
 * Proctoring Hook for AI Interviews
 * Tracks:
 * - Full-screen mode
 * - Copy/paste detection
 * - Tab switching
 * - Window blur
 * - Fullscreen exit
 * - Right-click attempts
 * - Shift key presses
 */
export const useProctoring = (sessionId, interviewId) => {
  const [proctoringEvents, setProctoringEvents] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [shiftClickCount, setShiftClickCount] = useState(0);
  const [rightClickCount, setRightClickCount] = useState(0);
  const [isRequestingFullscreen, setIsRequestingFullscreen] = useState(false);
  const eventsQueue = useRef([]);
  const isSendingEvents = useRef(false);
  const flushTimerRef = useRef(null);
  const BATCH_INTERVAL_MS = 6000;

  // Flush accumulated events to server in one combined call
  const flushEvents = useCallback(async () => {
    if (isSendingEvents.current || eventsQueue.current.length === 0) return;

    isSendingEvents.current = true;
    const eventsToSend = [...eventsQueue.current];
    eventsQueue.current = [];

    try {
      await axios.post('/api/analytics/events', {
        sessionId,
        interviewId,
        events: eventsToSend
      });
      logger.log(`📤 Sent ${eventsToSend.length} analytics events to server (batched)`);
    } catch (error) {
      logger.error('❌ Failed to send analytics events:', error);
      eventsQueue.current = [...eventsToSend, ...eventsQueue.current];
    } finally {
      isSendingEvents.current = false;
    }
  }, [sessionId, interviewId]);

  // Start a repeating 6-second flush timer once on mount
  useEffect(() => {
    flushTimerRef.current = setInterval(() => {
      flushEvents();
    }, BATCH_INTERVAL_MS);

    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
    };
  }, [flushEvents]);

  // Add event to queue (no immediate send — batched every 6s)
  const addProctoringEvent = useCallback((eventType, details = {}) => {
    const event = {
      id: Date.now() + Math.random(),
      sessionId,
      interviewId,
      eventType,
      timestamp: new Date().toISOString(),
      ...details
    };

    eventsQueue.current.push(event);
    setProctoringEvents(prev => [...prev, event]);

    logger.log('🔍 Proctoring Event (queued):', event);
  }, [sessionId, interviewId]);

  // Request fullscreen
  const requestFullscreen = useCallback(async () => {
    try {
      setIsRequestingFullscreen(true);
      const element = document.documentElement;
      
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if (element.webkitRequestFullscreen) {
        await element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        await element.msRequestFullscreen();
      } else if (element.mozRequestFullScreen) {
        await element.mozRequestFullScreen();
      }
      
      logger.log('🖥️ Fullscreen requested successfully');
    } catch (error) {
      logger.error('❌ Failed to request fullscreen:', error);
    } finally {
      setIsRequestingFullscreen(false);
    }
  }, []);

  // Block right-click and track attempts
  useEffect(() => {
    const handleContextMenu = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setRightClickCount(prev => prev + 1);
      addProctoringEvent('RIGHT_CLICK_DETECTED', {
        x: e.clientX,
        y: e.clientY,
        timestamp: new Date().toISOString()
      });
      return false;
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Shift' && !e.repeat) {
        addProctoringEvent('SHIFT_KEY_DETECTED', {
          key: e.key,
          code: e.code,
          timestamp: new Date().toISOString()
        });
      }
    };

    const handleMouseDown = (e) => {
      // Track Shift+Click
      if (e.shiftKey) {
        setShiftClickCount(prev => prev + 1);
        addProctoringEvent('SHIFT_CLICK_DETECTED', {
          button: e.button,
          x: e.clientX,
          y: e.clientY,
          timestamp: new Date().toISOString()
        });
      }
    };

    // Block right-click
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [addProctoringEvent]);

  // Copy/Paste detection
  useEffect(() => {
    const handleCopy = (e) => {
      addProctoringEvent('COPY_DETECTED', {
        content: e.clipboardData?.getData('text')?.substring(0, 100) || 'unknown',
        selector: window.getSelection()?.toString()?.substring(0, 100) || 'unknown'
      });
    };

    const handlePaste = (e) => {
      addProctoringEvent('PASTE_DETECTED', {
        content: e.clipboardData?.getData('text')?.substring(0, 100) || 'unknown'
      });
    };

    const handleCut = (e) => {
      addProctoringEvent('CUT_DETECTED', {
        content: window.getSelection()?.toString()?.substring(0, 100) || 'unknown'
      });
    };

    document.addEventListener('copy', handleCopy);
    document.addEventListener('paste', handlePaste);
    document.addEventListener('cut', handleCut);

    return () => {
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('cut', handleCut);
    };
  }, [addProctoringEvent]);

  // Tab switching and window blur detection
  useEffect(() => {
    let blurStartTime = null;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        blurStartTime = Date.now();
        addProctoringEvent('TAB_SWITCH_DETECTED', {
          action: 'hidden',
          timestamp: new Date().toISOString()
        });
      } else {
        const blurDuration = blurStartTime ? Date.now() - blurStartTime : 0;
        addProctoringEvent('TAB_RETURN_DETECTED', {
          action: 'visible',
          blurDuration,
          timestamp: new Date().toISOString()
        });
        blurStartTime = null;
      }
    };

    const handleBlur = () => {
      blurStartTime = Date.now();
      addProctoringEvent('WINDOW_BLUR_DETECTED', {
        timestamp: new Date().toISOString()
      });
    };

    const handleFocus = () => {
      const blurDuration = blurStartTime ? Date.now() - blurStartTime : 0;
      addProctoringEvent('WINDOW_FOCUS_DETECTED', {
        blurDuration,
        timestamp: new Date().toISOString()
      });
      blurStartTime = null;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [addProctoringEvent]);

  // Fullscreen change detection
  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenElement = document.fullscreenElement || 
                           document.webkitFullscreenElement || 
                           document.mozFullScreenElement || 
                           document.msFullscreenElement;
      
      const isCurrentlyFullscreen = !!fullscreenElement;
      setIsFullscreen(isCurrentlyFullscreen);

      if (isCurrentlyFullscreen) {
        addProctoringEvent('FULLSCREEN_ENTERED', {
          timestamp: new Date().toISOString()
        });
        logger.log('🖥️ Fullscreen entered');
      } else {
        addProctoringEvent('FULLSCREEN_EXITED', {
          timestamp: new Date().toISOString()
        });
        logger.log('🚪 Fullscreen exited');
        
        // Auto-request fullscreen again after exit
        setTimeout(() => {
          requestFullscreen();
        }, 1000);
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, [addProctoringEvent, requestFullscreen]);

  // Keyboard shortcuts detection (Ctrl+C, Ctrl+V, etc.)
  useEffect(() => {
    const handleKeyDown = (e) => {
      const key = e.key.toLowerCase();
      const ctrlKey = e.ctrlKey || e.metaKey;
      
      if (ctrlKey) {
        if (key === 'c') {
          addProctoringEvent('KEYBOARD_COPY_DETECTED', {
            key: 'Ctrl+C',
            timestamp: new Date().toISOString()
          });
        } else if (key === 'v') {
          addProctoringEvent('KEYBOARD_PASTE_DETECTED', {
            key: 'Ctrl+V',
            timestamp: new Date().toISOString()
          });
        } else if (key === 'x') {
          addProctoringEvent('KEYBOARD_CUT_DETECTED', {
            key: 'Ctrl+X',
            timestamp: new Date().toISOString()
          });
        }
      }
      
      // Alt+Tab detection
      if (e.altKey && key === 'tab') {
        addProctoringEvent('ALT_TAB_DETECTED', {
          timestamp: new Date().toISOString()
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [addProctoringEvent]);

  // Flush remaining events on unmount via sendBeacon (survives page teardown)
  useEffect(() => {
    return () => {
      if (flushTimerRef.current) clearInterval(flushTimerRef.current);
      if (eventsQueue.current.length > 0) {
        const payload = JSON.stringify({
          sessionId,
          interviewId,
          events: eventsQueue.current,
        });
        eventsQueue.current = [];
        if (navigator.sendBeacon) {
          const blob = new Blob([payload], { type: 'application/json' });
          navigator.sendBeacon('/api/analytics/events', blob);
        } else {
          try {
            const xhr = new XMLHttpRequest();
            xhr.open('POST', '/api/analytics/events', false);
            xhr.setRequestHeader('Content-Type', 'application/json');
            xhr.send(payload);
          } catch (_) {}
        }
      }
    };
  }, [sessionId, interviewId]);

  return {
    proctoringEvents,
    isFullscreen,
    isRequestingFullscreen,
    shiftClickCount,
    rightClickCount,
    requestFullscreen,
    addProctoringEvent
  };
};
